import {
  Box,
  Typography,
  Grid,
  Stack,
  InputAdornment,
  IconButton,
  Dialog,
  Button,
  TextField,
} from "@mui/material";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import axios from "../../axiosConfig";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { passwordValidationPattern } from "../../constants/index";
import MessageModal from "../MessageModal";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";

const loginFieldSx = {
  "& .MuiInputBase-root": {
    padding: 0,
    border: "1px solid #E4E9EF",
    borderRadius: "14px",
    overflow: "hidden",
    backgroundColor: "#fff",
    filter: "drop-shadow(0px 3px 3px rgba(0,0,0,0.10))",
  },
  "& input": {
    border: "none !important",
    filter: "none !important",
    boxShadow: "none !important",
    borderRadius: "0 !important",
  },
  "& .MuiInputAdornment-root": { m: 0 },
};

const EyeAdornment = ({ visible, onToggle }) => (
  <InputAdornment position="start">
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 48,
        height: 48,
        backgroundColor: "#EAF0F7",
      }}
    >
      <IconButton onClick={onToggle} size="small">
        {visible ? <VisibilityOffIcon color="primary" /> : <VisibilityIcon color="primary" />}
      </IconButton>
    </Box>
  </InputAdornment>
);

const ChangePassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [openFailureModal, setOpenFailureModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [customMessage, setCustomMessage] = useState("");
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith("ar");

  const { handleSubmit, control, watch, formState: { errors } } = useForm({ mode: "onChange" });
  const watchedPassword = watch("newPassword");

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      await axios.put("user/password", { oldPassword: data.currentPassword, newPassword: data.newPassword });
      setIsLoading(false);
      setOpenSuccessModal(true);
    } catch (error) {
      setIsLoading(false);
      setCustomMessage(error);
      setOpenFailureModal(true);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Stack gap={2} sx={{ "& .MuiInput-root": { backgroundColor: "inherit" } }}>

          {/* Row 1: current password */}
          <Grid sx={{ width: "100%" }} spacing={2} container>
            <Grid item md={5} xs={12}>
              <Typography sx={{ fontWeight: "bold", marginBottom: 1 }} color="primary">
                {isRTL ? "كلمة المرور الحالية" : "Current Password"}
              </Typography>
              <Controller
                name="currentPassword"
                control={control}
                rules={{ required: isRTL ? "يجب ادخال كلمة المرور الحالية" : "Required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    type={showCurrent ? "text" : "password"}
                    sx={loginFieldSx}
                    inputProps={{ style: { textAlign: "center", height: 48, padding: 0 } }}
                    InputProps={{
                      disableUnderline: true,
                      placeholder: isRTL ? "ادخل كلمة المرور" : "Enter password",
                      startAdornment: <EyeAdornment visible={showCurrent} onToggle={() => setShowCurrent(p => !p)} />,
                    }}
                    FormHelperTextProps={{ sx: { textAlign: "start" } }}
                    error={!!errors.currentPassword}
                    helperText={errors.currentPassword?.message || ""}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Row 2: new password + confirm */}
          <Grid sx={{ width: "100%" }} spacing={2} container>
            <Grid item md={5} xs={12}>
              <Typography sx={{ fontWeight: "bold", marginBottom: 1 }} color="primary">
                {isRTL ? "كلمة المرور الجديدة" : "New Password"}
              </Typography>
              <Controller
                name="newPassword"
                control={control}
                rules={{
                  required: isRTL ? "يجب ادخال كلمة المرور الجديدة" : "Required",
                  pattern: {
                    value: passwordValidationPattern,
                    message: isRTL
                      ? "يجب أن تحتوي على 8 خانات على الأقل، حروف كبيرة وصغيرة وأرقام"
                      : "Min 8 chars, upper/lower case and numbers",
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    type={showNew ? "text" : "password"}
                    sx={loginFieldSx}
                    inputProps={{ style: { textAlign: "center", height: 48, padding: 0 } }}
                    InputProps={{
                      disableUnderline: true,
                      placeholder: isRTL ? "ادخل كلمة المرور" : "Enter password",
                      startAdornment: <EyeAdornment visible={showNew} onToggle={() => setShowNew(p => !p)} />,
                    }}
                    FormHelperTextProps={{ sx: { textAlign: "start" } }}
                    error={!!errors.newPassword}
                    helperText={errors.newPassword?.message || ""}
                  />
                )}
              />
            </Grid>
            <Grid item md={5} xs={12}>
              <Typography sx={{ fontWeight: "bold", marginBottom: 1 }} color="primary">
                {isRTL ? "تأكيد كلمة المرور" : "Confirm Password"}
              </Typography>
              <Controller
                name="confirmPassword"
                control={control}
                rules={{
                  required: isRTL ? "يجب تأكيد كلمة المرور" : "Required",
                  validate: (v) => v === watchedPassword || (isRTL ? "كلمة المرور غير مطابقة" : "Passwords do not match"),
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    type={showConfirm ? "text" : "password"}
                    sx={loginFieldSx}
                    inputProps={{ style: { textAlign: "center", height: 48, padding: 0 } }}
                    InputProps={{
                      disableUnderline: true,
                      placeholder: isRTL ? "ادخل كلمة المرور" : "Enter password",
                      startAdornment: <EyeAdornment visible={showConfirm} onToggle={() => setShowConfirm(p => !p)} />,
                    }}
                    FormHelperTextProps={{ sx: { textAlign: "start" } }}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message || ""}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Stack>

        <Box sx={{ display: "flex", flexDirection: "row", pt: 5, gap: 2, justifyContent: "end" }}>
          <LoadingButton
            type="submit"
            sx={{ width: "20%", alignSelf: "center", borderRadius: "14px" }}
            variant="contained"
            loading={isLoading}
          >
            <Typography>{isRTL ? "حفظ" : "Save"}</Typography>
          </LoadingButton>
        </Box>
      </form>

      <MessageModal
        openModal={openFailureModal}
        message={customMessage?.response?.data?.message || (isRTL ? "لقد حدث خطأ أثناء عملية تغيير كلمة المرور، يرجى المحاولة لاحقاً" : "An error occurred. Please try again later.")}
        buttonText={isRTL ? "إغلاق" : "Close"}
        interfaceIcon={<CancelIcon color="primary" sx={{ height: 80, width: 80 }} />}
        onCloseModal={() => setOpenFailureModal(false)}
      />

      <Dialog
        open={openSuccessModal}
        maxWidth="md"
        fullWidth
        sx={{ "& .MuiDialog-paper": { border: "1px solid primary.main", borderRadius: 3, padding: 3 } }}
      >
        <Stack justifyContent="center" alignItems="center">
          <CheckCircleIcon color="primary" sx={{ height: 80, width: 80 }} />
          <Typography sx={{ fontWeight: "bold" }} variant="h6">
            {isRTL ? "تم تغيير كلمة المرور بنجاح" : "Password changed successfully"}
          </Typography>
          <Button
            type="submit"
            sx={{ width: "30%", alignSelf: "center", marginTop: 4, borderRadius: "14px" }}
            variant="contained"
            onClick={() => navigate("/home")}
          >
            <Typography>{isRTL ? "العودة إلى لوحة البيانات" : "Go to Dashboard"}</Typography>
          </Button>
        </Stack>
      </Dialog>
    </>
  );
};

export default ChangePassword;
